/**
 * AccountSetupPage - Create password after Stripe checkout
 * MODULE: core/auth/frontend/pages/AccountSetupPage.tsx
 */
import { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, TextInput, Label, Button, Spinner } from 'flowbite-react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;
const SUBSCRIPTION_BASE = `${getApiBaseUrl()}/api/v1/auth/subscription`;

function getStrength(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  return Math.min(score, 4);
}

export function AccountSetupPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [strength, setStrength] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError('Missing session. Go to pricing to get started.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SUBSCRIPTION_BASE}/session?session_id=${encodeURIComponent(sessionId)}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data.email) setEmail(data.email);
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword || strength < 3) return;
    setError(null);
    setSubmitting(true);
    try {
      const registerRes = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          full_name: fullName.trim() || undefined,
        }),
      });
      const regData = await registerRes.json().catch(() => ({}));
      if (!registerRes.ok) throw new Error(regData.detail || 'Registration failed');
      const userId = regData.user_id;
      const resolution = regData.tenant_resolution;
      if (resolution?.action === 'PROMPT_JOIN_OR_CREATE' && resolution?.existing_tenant) {
        navigate('/tenant-select', { state: { userId, tenantResolution: resolution, email: email.trim() } });
      } else {
        navigate('/tenant-select', { state: { userId, tenantResolution: { action: 'CREATE_NEW_TENANT' }, email: email.trim() } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full mx-auto p-8 text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </Card>
      </div>
    );
  }

  const canSubmit = strength >= 3 && password === confirmPassword && password.length >= 8 && email;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="max-w-md w-full mx-auto p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Step 1 of 4</p>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create your account</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-6">Set up your password to secure your account.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="text-sm text-red-600 dark:text-red-200 bg-red-50 dark:bg-red-800/50 p-2 rounded">{error}</div>}
          <div>
            <Label htmlFor="email">Email</Label>
            <TextInput id="email" type="email" value={email} disabled className="mt-1 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </div>
          <div>
            <Label htmlFor="fullName">Full name (optional)</Label>
            <TextInput
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={submitting}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <TextInput
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              className="mt-1"
            />
            <div className="mt-2">
              <PasswordStrengthIndicator password={password} onStrengthChange={setStrength} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Password strength must be Good or better.</p>
          </div>
          <div>
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <TextInput
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              className="mt-1"
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-sm text-red-600 mt-1">Passwords don&apos;t match</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={!canSubmit || submitting}>
            {submitting ? <><Spinner size="sm" className="mr-2" /> Continuing...</> : 'Continue'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
